/**
 * ModeSelectionControl Component
 *
 * A specialized form field component for selecting between different modes/options
 * with visual button representations (similar to a segmented control or button group).
 *
 * Features:
 * - Single or multiple selection
 * - Flexible layouts (horizontal, vertical, grid)
 * - Icon and description support
 * - Field-level validation
 * - Full theme integration (NO hardcoded styles)
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  FormHelperText,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface ModeOption<TValue = string> {
  value: TValue;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  metadata?: Record<string, any>;
  /**
   * `data-testid` for this option's button. Consumers MUST take the value from
   * the shared selector registry (`modules/shared-test-navigation/MODULE.md`),
   * never hardcode it here.
   */
  testId?: string;
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
  value: any;
  timestamp?: Date;
}

export interface ValidationRule {
  type: string;
  value?: any;
  message: string;
  validator?: (value: any, data?: any) => boolean | Promise<boolean>;
  async?: boolean;
}

export interface ModeSelectionControlProps<TValue = string> {
  /**
   * Core Functionality
   */
  value: TValue | TValue[];
  onChange: (value: TValue | TValue[]) => void;
  options: ModeOption<TValue>[];

  /**
   * Selection Behavior
   */
  allowMultiple?: boolean;

  /**
   * Form Integration
   */
  name?: string;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;

  /**
   * Display Configuration
   */
  layout?: 'horizontal' | 'vertical' | 'grid';
  columns?: number;
  spacing?: number;

  /**
   * Visual Style
   */
  buttonVariant?: 'contained' | 'outlined' | 'text';
  buttonSize?: 'small' | 'medium' | 'large';
  showIcons?: boolean;
  showDescriptions?: boolean;
  /**
   * Typography variant of the option LABEL (the description always renders as
   * `caption`). Defaults to `body2`; `body1` gives a 16px/12px pair, which is
   * the step ratio the design detector's type-hierarchy rule expects.
   */
  optionLabelVariant?: 'body1' | 'body2';

  /**
   * Validation (Field-Level Responsibility)
   */
  error?: boolean;
  errorMessage?: string;
  rules?: ValidationRule[];
  validateOnChange?: boolean;
  onValidationChange?: (errors: ValidationError[]) => void;

  /**
   * Theme Integration (REQUIRED - no hardcoded styles)
   */
  sx?: SxProps<Theme>;
  variant?: 'default' | 'compact' | 'elevated';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

  /**
   * Accessibility
   */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-labelledby'?: string;

  /** `data-testid` for the control root. Take the value from the shared registry. */
  'data-testid'?: string;

  /**
   * Event Handlers
   */
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

const validateValue = <TValue,>(
  value: TValue | TValue[],
  rules: ValidationRule[] = [],
  options: ModeOption<TValue>[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  rules.forEach((rule) => {
    let isValid = false;

    switch (rule.type) {
      case 'required':
        const isEmpty = Array.isArray(value) ? value.length === 0 : value == null || value === '';
        isValid = !isEmpty;
        break;
      case 'custom':
        if (rule.validator) {
          const result = rule.validator(value);
          isValid = result instanceof Promise ? false : Boolean(result);
        }
        break;
      default:
        // For any other validation types, just pass through
        isValid = true;
        break;
    }

    if (!isValid) {
      errors.push({
        field: 'value',
        rule: rule.type,
        message: rule.message,
        value,
        timestamp: new Date(),
      });
    }
  });

  return errors;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ModeSelectionControl = <TValue extends string | number = string>({
  value,
  onChange,
  options,
  allowMultiple = false,
  name,
  label,
  helperText,
  required = false,
  disabled = false,
  readOnly = false,
  layout = 'horizontal',
  columns = 2,
  spacing = 1,
  buttonVariant = 'outlined',
  buttonSize = 'medium',
  showIcons = true,
  showDescriptions = false,
  optionLabelVariant = 'body2',
  error = false,
  errorMessage,
  rules = [],
  validateOnChange = true,
  onValidationChange,
  sx,
  variant = 'default',
  color = 'primary',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
  onFocus,
  onBlur,
}: ModeSelectionControlProps<TValue>) => {
  const theme = useTheme();

  // ========================================================================
  // STATE MANAGEMENT
  // ========================================================================

  const [focused, setFocused] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // ========================================================================
  // COMPUTED VALUES
  // ========================================================================

  const normalizedValue = useMemo(() => {
    if (allowMultiple) {
      return Array.isArray(value) ? value : (value != null ? [value] : []);
    }
    return Array.isArray(value) ? value[0] : value;
  }, [value, allowMultiple]);

  const isSelected = useCallback((optionValue: TValue) => {
    if (allowMultiple) {
      const valueArray = Array.isArray(normalizedValue) ? normalizedValue : [normalizedValue];
      return valueArray.includes(optionValue);
    }
    return normalizedValue === optionValue;
  }, [normalizedValue, allowMultiple]);

  const hasError = error || validationErrors.length > 0;
  const displayError = errorMessage || validationErrors[0]?.message;

  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  const handleOptionClick = useCallback((optionValue: TValue) => {
    if (disabled || readOnly) return;

    let newValue: TValue | TValue[];

    if (allowMultiple) {
      const currentArray = Array.isArray(normalizedValue) ? normalizedValue : [normalizedValue].filter(v => v != null);
      const isCurrentlySelected = currentArray.includes(optionValue);

      if (isCurrentlySelected) {
        newValue = currentArray.filter(v => v !== optionValue);
      } else {
        newValue = [...currentArray, optionValue];
      }
    } else {
      newValue = optionValue;
    }

    // Validate on change if enabled
    if (validateOnChange) {
      const errors = validateValue(newValue, rules, options);
      setValidationErrors(errors);
      onValidationChange?.(errors);
    }

    onChange(newValue);
  }, [disabled, readOnly, allowMultiple, normalizedValue, validateOnChange, rules, options, onChange, onValidationChange]);

  const handleFocus = useCallback((event: React.FocusEvent) => {
    setFocused(true);
    onFocus?.(event);
  }, [onFocus]);

  const handleBlur = useCallback((event: React.FocusEvent) => {
    setFocused(false);
    onBlur?.(event);
  }, [onBlur]);

  // ========================================================================
  // DYNAMIC STYLES (THEME-AWARE)
  // ========================================================================

  const containerStyles: SxProps<Theme> = useMemo(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    ...sx,
  }), [theme, sx]);

  const optionsContainerStyles: SxProps<Theme> = useMemo(() => {
    const baseStyles: SxProps<Theme> = {
      display: layout === 'grid' ? 'grid' : 'flex',
      gap: theme.spacing(spacing),
    };

    switch (layout) {
      case 'horizontal':
        return {
          ...baseStyles,
          flexDirection: 'row',
          flexWrap: 'wrap',
        };
      case 'vertical':
        return {
          ...baseStyles,
          flexDirection: 'column',
        };
      case 'grid':
        return {
          ...baseStyles,
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
        };
      default:
        return baseStyles;
    }
  }, [layout, theme, spacing, columns]);

  const getButtonStyles = useCallback((optionValue: TValue, option: ModeOption<TValue>): SxProps<Theme> => {
    const selected = isSelected(optionValue);
    const isDisabled = disabled || option.disabled;

    return {
      minHeight: theme.spacing(6),
      padding: theme.spacing(1, 2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: selected
        ? alpha(theme.palette[color].main, 0.1)
        : theme.palette.background.paper,
      borderColor: selected
        ? theme.palette[color].main
        : theme.palette.divider,
      // The selected state is carried by the accent BORDER + tint fill + medium
      // weight, never by accent-coloured label text: `palette[color].main` on
      // its own 8% tint measures 4.15:1 and fails AA for small text
      // (strategy-blocks designer invariant 15).
      color: theme.palette.text.primary,
      '&:hover': !isDisabled ? {
        backgroundColor: selected
          ? alpha(theme.palette[color].main, 0.15)
          : alpha(theme.palette.action.hover, 0.05),
        borderColor: selected
          ? theme.palette[color].main
          : alpha(theme.palette.action.hover, 0.25),
      } : {},
      // `:focus-visible`, not `:focus`: a mouse click left a permanent 2px ring
      // around the chosen option (seen in the T7b design review). Keyboard users
      // still get the ring.
      '&:focus-visible': {
        outline: `2px solid ${theme.palette[color].main}`,
        outlineOffset: theme.spacing(0.25),
      },
      ...(variant === 'elevated' && {
        boxShadow: selected ? theme.shadows[2] : theme.shadows[1],
        '&:hover': !isDisabled ? {
          boxShadow: theme.shadows[3],
        } : {},
      }),
      ...(variant === 'compact' && {
        minHeight: theme.spacing(4),
        padding: theme.spacing(0.5, 1.5),
      }),
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      transition: theme.transitions.create([
        'background-color',
        'border-color',
        'box-shadow',
        'opacity'
      ], {
        duration: theme.transitions.duration.short,
      }),
    };
  }, [isSelected, disabled, theme, color, variant]);

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <FormControl
      error={hasError}
      disabled={disabled}
      required={required}
      sx={containerStyles}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid || hasError}
      data-testid={dataTestId}
    >
      {label && (
        <FormLabel
          component="legend"
          sx={{
            color: hasError
              ? theme.palette.error.main
              : focused
                ? theme.palette[color].main
                : theme.palette.text.primary,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            marginBottom: theme.spacing(1),
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: theme.palette.error.main,
                marginLeft: theme.spacing(0.5),
              }}
            >
              *
            </Typography>
          )}
        </FormLabel>
      )}

      <Box sx={optionsContainerStyles}>
        {options.map((option) => (
          <Button
            key={String(option.value)}
            variant={buttonVariant}
            size={buttonSize}
            disabled={disabled || option.disabled}
            onClick={() => handleOptionClick(option.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            sx={getButtonStyles(option.value, option)}
            startIcon={showIcons && option.icon ? option.icon : undefined}
            data-testid={option.testId}
            aria-pressed={isSelected(option.value)}
            aria-describedby={option.description ? `${name}-${option.value}-desc` : undefined}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left',
              width: '100%',
            }}>
              <Typography
                variant={optionLabelVariant}
                sx={{
                  fontWeight: isSelected(option.value)
                    ? theme.typography.fontWeightMedium
                    : theme.typography.fontWeightRegular,
                }}
              >
                {option.label}
              </Typography>
              {showDescriptions && option.description && (
                <Typography
                  id={`${name}-${option.value}-desc`}
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    marginTop: theme.spacing(0.5),
                    fontSize: theme.typography.caption.fontSize,
                  }}
                >
                  {option.description}
                </Typography>
              )}
            </Box>
          </Button>
        ))}
      </Box>

      {(helperText || displayError) && (
        <FormHelperText
          sx={{
            color: hasError
              ? theme.palette.error.main
              : theme.palette.text.secondary,
            fontSize: theme.typography.caption.fontSize,
            marginTop: theme.spacing(0.5),
          }}
        >
          {displayError || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// ============================================================================
// MEMOIZATION
// ============================================================================

export default React.memo(ModeSelectionControl, (prevProps, nextProps) => {
  // Custom comparison function for performance optimization
  const compareArrays = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
  };

  return (
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.error === nextProps.error &&
    prevProps.errorMessage === nextProps.errorMessage &&
    prevProps.label === nextProps.label &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.required === nextProps.required &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.allowMultiple === nextProps.allowMultiple &&
    prevProps.layout === nextProps.layout &&
    prevProps.buttonVariant === nextProps.buttonVariant &&
    prevProps.showIcons === nextProps.showIcons &&
    prevProps.showDescriptions === nextProps.showDescriptions &&
    prevProps.optionLabelVariant === nextProps.optionLabelVariant &&
    prevProps['data-testid'] === nextProps['data-testid'] &&
    compareArrays(prevProps.options, nextProps.options) &&
    compareArrays(prevProps.rules || [], nextProps.rules || [])
  );
});

// Types are exported above with their definitions